import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

// ---------------------------------------------------------------------------
// Types — mirror the backend serializers (OWNED_JOBS_PLAN.md §1):
//   service_service.serialize, service_version_service.serialize,
//   jobs/job_service.serialize, jobs/job_events.serialize_event,
//   jobs/guidance_service.serialize_guidance / serialize_approval,
//   jobs/routine_service.serialize_routine, routes/jobs._serialize_wake.
// ---------------------------------------------------------------------------

/**
 * Org-server binding (MCP_SERVERS_PLAN.md): a version references an
 * organization MCP server by id; url/credential/catalog live on the server.
 */
export interface McpConnection {
    mcp_server_id: string;
    slug?: string | null;
    enabled?: boolean;
    enabled_tools?: string[] | null;
}

export type ApprovalPolicy = 'auto' | 'ask_owner' | 'ask_vendor';

export interface ServiceTools {
    mcp: McpConnection[];
    native: string[];
    crm_read: boolean;
    web: boolean;
    knowledge: boolean;
    code: boolean;
    /** LLM tool name ("{slug}_{tool}") or "*" → policy for non-read-only vendor tools. */
    write_policy: Record<string, ApprovalPolicy>;
}

export const EMPTY_SERVICE_TOOLS: ServiceTools = {
    mcp: [],
    native: [],
    crm_read: false,
    web: true,
    knowledge: true,
    code: false,
    write_policy: {},
};

const mcpIdentity = (entry: McpConnection): string | null =>
    entry.mcp_server_id ? `server:${entry.mcp_server_id}` : null;

const mcpSlug = (entry: McpConnection): string => {
    if (typeof entry.slug === 'string' && entry.slug.trim()) return entry.slug.trim();
    return 'mcp';
};

const uniquifySlug = (slug: string, used: Set<string>): string => {
    if (!used.has(slug)) return slug;
    const base = slug.slice(0, 21);
    let attempt = 2;
    let candidate = `${base}_${attempt}`;
    while (used.has(candidate)) {
        attempt += 1;
        candidate = `${base}_${attempt}`;
    }
    return candidate;
};

/**
 * Seed-from-agent copies the agent's MCP bindings into the draft, but must
 * not drop bindings the editor already added. Existing entries win on the
 * same server; incoming slugs are bumped if they collide so tool names stay
 * unique on the version.
 */
export function mergeSeededMcp(
    existing: McpConnection[] | null | undefined,
    seeded: McpConnection[] | null | undefined,
): McpConnection[] {
    const current = Array.isArray(existing) ? existing : [];
    const incoming = Array.isArray(seeded) ? seeded : [];
    const merged = [...current];
    const seen = new Set(current.map(mcpIdentity).filter((key): key is string => Boolean(key)));
    const usedSlugs = new Set(current.map(mcpSlug));

    for (const entry of incoming) {
        if (!entry || typeof entry !== 'object') continue;
        const identity = mcpIdentity(entry);
        if (identity && seen.has(identity)) continue;
        if (identity) seen.add(identity);
        const slug = uniquifySlug(mcpSlug(entry), usedSlugs);
        usedSlugs.add(slug);
        merged.push(slug === entry.slug ? entry : { ...entry, slug });
    }
    return merged;
}

/** The LLM-facing name of a vendor MCP tool: "{slug}_{tool}" (mcp_client_service). */
export const llmToolName = (slug: string | null | undefined, toolName: string): string =>
    `${slug || 'mcp'}_${toolName}`.slice(0, 64);

// 'array' = list of strings; with `enum` it renders as a multi-select.
export type InputFieldType = 'string' | 'number' | 'integer' | 'boolean' | 'array';

export interface InputFieldSpec {
    type: InputFieldType;
    description?: string;
    enum?: (string | number)[];
    default?: string | number | boolean | string[];
    items?: { type: 'string' };
}

/** Flat JSON-schema object (guidance input_schema). */
export interface ServiceSchema {
    type: 'object';
    properties: Record<string, InputFieldSpec>;
    required?: string[];
}

export interface StateFieldSpec {
    type: InputFieldType;
    description?: string;
}

/** version.state_schema: the typed headline fields + a headline template. */
export interface StateSchema {
    fields: Record<string, StateFieldSpec>;
    headline?: string | null;
}

export const EMPTY_STATE_SCHEMA: StateSchema = { fields: {}, headline: null };

export interface SetupQuestion {
    key: string;
    question: string;
    type: InputFieldType;
    options?: (string | number)[];
    required: boolean;
}

export type RoutineKind = 'job' | 'code' | 'agent';
export type ScheduleFrequency = 'interval' | 'hourly' | 'daily' | 'weekly' | 'monthly';

export type RoutineTrigger =
    | {
          type: 'schedule';
          frequency: ScheduleFrequency;
          at?: string;
          weekday?: number;
          day_of_month?: number;
          interval_minutes?: number;
          timezone?: string;
      }
    | { type: 'event'; name: string };

/** A version's default routine (copied into every job at assignment). */
export interface RoutineSpec {
    name: string;
    kind: RoutineKind;
    trigger: RoutineTrigger;
    wake_parent?: 'never' | 'on_request' | 'always';
    script_path?: string;
    instructions?: string;
    model?: string | null;
    budget_per_run?: number | null;
}

export interface RoutineLimits {
    max_per_job?: number;
    min_interval_minutes?: number;
    allow_agent_routines?: boolean;
    allowed_models?: string[];
}

export type Completion = 'standing' | 'ai';

export interface Budget {
    period?: 'day' | 'week' | 'month';
    target_credits?: number;
    max_credits?: number;
    max_credits_per_wake?: number;
}

export interface Delivery {
    owner?: ('in_app' | 'email')[];
    stewards?: ('email' | 'slack')[];
    slack_channel_id?: string;
}

/** What the customer agent reads to route to a service (version.routing):
 * the asks a running job owns, and when to offer starting one. Agent-facing;
 * customers never see it. */
export interface Routing {
    handles?: string;
    offer_when?: string;
}

export type ServiceStatus = 'active' | 'disabled' | 'archived';
export type Acquisition = 'vendor_assigned' | 'included' | 'request' | 'stripe';
export type JobScope = 'user' | 'tenant';
/** live = acts; shadow = rehearses (writes and customer messages are recorded as "would have"). */
export type JobMode = 'live' | 'shadow';

export interface GuidanceDefaults {
    reminder_hours?: number;
    deadline_hours?: number;
}

/** service.playbook: accumulated service-scope guidance answers. */
export interface PlaybookEntry {
    question?: string;
    answer?: string;
    answered_by?: string | null;
    answered_at?: string | null;
    request_id?: string | null;
    job_id?: string | null;
    [key: string]: unknown;
}

/** GET /services/ rows (service_service.serialize). */
export interface Service {
    id: string;
    key: string;
    internal_name: string;
    name: string;
    short_description?: string | null;
    description?: string | null;
    acquisition: Acquisition;
    visible: boolean;
    scope: JobScope;
    default_job_mode: JobMode;
    start_on_events: string[];
    steward_user_ids: string[];
    guidance_defaults: GuidanceDefaults;
    playbook: PlaybookEntry[];
    status: ServiceStatus;
    source_agent_id?: string | null;
    current_published_version_id?: string | null;
    published_version_number?: number | null;
    completion?: Completion | null;
    has_draft: boolean;
    jobs_on: number;
    jobs_off: number;
    jobs_blocked: number;
    /** On jobs still rehearsing (shadow mode). */
    jobs_shadow?: number;
    last_wake_at?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
}

/** The live (non-versioned) service fields the editor auto-saves with
 * PUT /services/{id}. The team fields (steward_user_ids, guidance_defaults)
 * are edited on the Team tab but travel in the same live record. */
export interface ListingPayloadFields {
    internal_name: string;
    name: string;
    short_description: string;
    description: string;
    acquisition: Acquisition;
    visible: boolean;
    scope: JobScope;
    default_job_mode: JobMode;
    start_on_events: string[];
    steward_user_ids: string[];
    guidance_defaults: GuidanceDefaults;
}

/** Body for the listing auto-save. Every field the Listing and Team tabs
 * edit travels on every save: the server treats an absent field as "leave
 * it alone" and still answers 200, so a field missing here saves as a
 * silent no-op (the UI says Saved, a reload drops the value). That is how
 * start_on_events and default_job_mode went missing. */
export const listingPayload = (fields: ListingPayloadFields): Record<string, unknown> => ({
    ...(fields.internal_name.trim() ? { internal_name: fields.internal_name.trim() } : {}),
    ...(fields.name.trim() ? { name: fields.name.trim() } : {}),
    short_description: fields.short_description || null,
    description: fields.description || null,
    acquisition: fields.acquisition,
    visible: fields.visible,
    scope: fields.scope,
    default_job_mode: fields.default_job_mode,
    start_on_events: fields.start_on_events,
    steward_user_ids: fields.steward_user_ids,
    guidance_defaults: fields.guidance_defaults,
});

/** One draft or one immutable published version (service_version_service.serialize). */
export interface ServiceVersion {
    id: string;
    service_id: string;
    state: 'draft' | 'published';
    version_number?: number | null;
    based_on_version_id?: string | null;
    instructions: string;
    guardrails: string;
    tools: Partial<ServiceTools>;
    state_schema: Partial<StateSchema>;
    routines: RoutineSpec[];
    routine_limits: RoutineLimits;
    setup: SetupQuestion[];
    completion: Completion;
    goal?: string | null;
    budget: Budget;
    delivery: Delivery;
    routing?: Routing;
    created_at?: string | null;
    updated_at?: string | null;
    published_at?: string | null;
}

/** GET /services/{id} (_service_detail). */
export interface ServiceDetail extends Service {
    published?: ServiceVersion | null;
    draft?: ServiceVersion | null;
    price?: { id: string; status: 'draft' | 'active' | 'archived'; label: string } | null;
}

/** PUT /services/{id}/draft body (routes/services.DraftFields). */
export interface DraftFields {
    instructions: string;
    guardrails: string;
    tools: ServiceTools;
    state_schema: StateSchema;
    routines: RoutineSpec[];
    routine_limits: RoutineLimits;
    setup: SetupQuestion[];
    completion: Completion;
    goal: string | null;
    budget: Budget;
    delivery: Delivery;
    routing: Routing;
}

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

export type JobStatus = 'on' | 'off';
export type BlockedOn = 'owner_input' | 'steward_guidance' | 'approval' | 'budget' | 'owner_missing';
export type EndedBy = 'ai' | 'owner' | 'vendor' | 'subscription';

export interface JobPerson {
    id: string;
    external_id?: string | null;
    email?: string | null;
    display_name?: string | null;
    status?: string | null;
}

export interface JobTenant {
    id: string;
    external_id?: string | null;
    name?: string | null;
    status?: string | null;
}

/** jobs/job_service.serialize (vendor dashboard shape). */
export interface Job {
    id: string;
    service_id: string;
    service_key?: string | null;
    service_name?: string | null;
    version_id?: string | null;
    version_number?: number | null;
    scope: JobScope;
    scope_id: string;
    customer_tenant_id?: string | null;
    tenant?: JobTenant | null;
    owner?: JobPerson | null;
    steward_user_ids?: string[] | null;
    status: JobStatus;
    mode: JobMode;
    /** Set when the job went live with a clean slate: events before it are the rehearsal record. */
    restarted_at?: string | null;
    blocked_on?: BlockedOn | null;
    blocked_detail?: Record<string, any> | null;
    ended_by?: EndedBy | null;
    ended_reason?: string | null;
    ended_at?: string | null;
    headline?: string | null;
    state: Record<string, any>;
    details: Record<string, any>;
    state_updated_at?: string | null;
    setup_answers: Record<string, any>;
    setup_completed_at?: string | null;
    next_wake_at?: string | null;
    next_wake_reason?: string | null;
    next_wake_note?: string | null;
    pending_wakes: any[];
    active_wake_id?: string | null;
    last_wake_at?: string | null;
    last_wake_summary?: string | null;
    wake_count?: number | null;
    budget_period_start?: string | null;
    credits_used_period: number;
    source?: string | null;
    order_id?: string | null;
    active_until?: string | null;
    created_from_conversation_id?: string | null;
    metadata: Record<string, any>;
    created_at?: string | null;
    updated_at?: string | null;
}

export type JobEventKind =
    | 'state_changed'
    | 'action'
    | 'artifact_published'
    | 'asked_owner'
    | 'asked_steward'
    | 'approval_requested'
    | 'approval_decided'
    | 'looped_in'
    | 'messaged'
    | 'blocked'
    | 'unblocked'
    | 'steered'
    | 'guided'
    | 'answered'
    | 'routine_changed'
    | 'wake_started'
    | 'wake_finished'
    | 'wake_failed'
    | 'wake_scheduled'
    | 'turned_on'
    | 'turned_off'
    | 'owner_changed'
    | 'memory_changed'
    | 'shadow_action'
    | 'shadow_message'
    | 'mode_changed'
    | 'restarted'
    | 'note';

/** jobs/job_events.serialize_event */
export interface JobEvent {
    id: string;
    job_id: string;
    wake_id?: string | null;
    seq: number;
    kind: JobEventKind | string;
    summary: string;
    details?: Record<string, any> | string | null;
    visibility?: 'owner' | 'tenant' | 'vendor' | null;
    actor?: string | null;
    evidence?: string[] | null;
    created_at?: string | null;
}

/** jobs/job_service.serialize_message */
export interface JobMessage {
    id: string;
    author: 'owner' | 'member' | 'steward' | 'agent' | 'job' | string;
    kind: string;
    text: string;
    wake_id?: string | null;
    handled_at?: string | null;
    created_at?: string | null;
}

/** jobs/job_service.serialize_artifact */
export interface JobArtifact {
    id: string;
    kind: 'markdown' | 'html' | 'file' | 'link' | string;
    title: string;
    version?: number | null;
    url?: string | null;
    content_type?: string | null;
    size_bytes?: number | null;
    visibility?: 'owner' | 'tenant' | 'vendor' | null;
    share_token?: string | null;
    created_at?: string | null;
    /** Only on GET /jobs/{id}/artifacts/{aid}. */
    body?: string | null;
}

/** jobs/guidance_service.serialize_guidance */
export interface GuidanceRequest {
    id: string;
    job_id: string;
    audience: 'owner' | 'steward';
    mode: 'blocking' | 'ask_and_continue';
    question: string;
    context?: string | null;
    input_schema?: ServiceSchema | null;
    options?: string[] | null;
    default_action?: string | null;
    deadline_at?: string | null;
    status: 'open' | 'answered' | 'defaulted' | 'withdrawn' | 'expired';
    answer?: Record<string, any> | null;
    answer_text?: string | null;
    answered_at?: string | null;
    created_at?: string | null;
    service_id?: string;
    wake_id?: string | null;
    answer_scope?: 'job' | 'service' | null;
    coalesced_count?: number | null;
    reminded_at?: string | null;
}

/** jobs/guidance_service.serialize_approval */
export interface JobApproval {
    id: string;
    job_id: string;
    policy: ApprovalPolicy;
    tool: string;
    args?: Record<string, any> | null;
    reason?: string | null;
    status: 'pending' | 'approved' | 'rejected' | 'expired' | 'executed' | 'failed';
    decision_note?: string | null;
    decided_at?: string | null;
    executed_at?: string | null;
    expires_at?: string | null;
    created_at?: string | null;
    wake_id?: string | null;
    result?: any;
}

/** jobs/routine_service.serialize_routine */
export interface JobRoutine {
    id: string;
    job_id: string;
    name: string;
    kind: RoutineKind;
    trigger: RoutineTrigger;
    trigger_label: string;
    script_path?: string | null;
    instructions?: string | null;
    model?: string | null;
    budget_per_run?: number | null;
    wake_parent?: string | null;
    status: 'active' | 'paused' | 'removed';
    status_reason?: string | null;
    from_version?: number | null;
    next_run_at?: string | null;
    last_run_at?: string | null;
    consecutive_failures?: number | null;
    created_at?: string | null;
}

export interface WakePlanItem {
    id: string;
    title: string;
    status: 'pending' | 'in_progress' | 'done' | 'skipped';
    note?: string | null;
    updated_at?: string | null;
}

export interface WakePlan {
    version: number;
    updated_at?: string | null;
    items: WakePlanItem[];
}

export interface WakeStep {
    kind?: 'tool' | 'model' | 'thought' | 'result' | string;
    iteration?: number;
    tool?: string | null;
    args?: string;
    result_preview?: string;
    thought?: string | null;
    error?: string | null;
    duration_ms?: number | null;
}

/** routes/jobs._serialize_wake */
export interface Wake {
    id: string;
    job_id?: string | null;
    reason?: string | null;
    mode?: 'job' | 'agent' | 'script' | string | null;
    routine_id?: string | null;
    status: string;
    segment?: number | null;
    summary?: string | null;
    stop_reason?: string | null;
    error?: { type?: string | null; message?: string | null } | null;
    plan?: WakePlan | null;
    input_request?: Record<string, any> | null;
    credits?: number | null;
    cost_usd?: number | null;
    queued_at?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
    created_at?: string | null;
    /** detail only */
    input?: Record<string, any> | null;
    steps?: WakeStep[];
    ai?: { model?: string; usage?: Record<string, number>; calls?: number } | null;
}

/** GET /jobs/{id} */
export interface JobDetail extends Job {
    service?: Service | null;
    state_schema: Partial<StateSchema>;
    effective_stewards: string[];
    events: JobEvent[];
    routines: JobRoutine[];
    open_questions: GuidanceRequest[];
    pending_approvals: JobApproval[];
    messages: JobMessage[];
    artifacts: JobArtifact[];
    wakes: Wake[];
}

export interface JobRef {
    id: string;
    service_id: string;
    service_name?: string | null;
    headline?: string | null;
    subject?: string | null;
}

/** GET /jobs/needs-you */
export interface NeedsYouPayload {
    guidance: (GuidanceRequest & { job: JobRef | null })[];
    approvals: (JobApproval & { job: JobRef | null })[];
    blocked_jobs: (Job & { job: JobRef | null })[];
}

/** GET /jobs/directory?q= */
export interface DirectoryUser {
    id: string;
    external_id?: string | null;
    email?: string | null;
    display_name?: string | null;
    tenants: { id: string; external_id?: string | null; name?: string | null }[];
}

/** GET /users/ (subset). */
export interface OrgUser {
    id: string;
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
}

export const orgUserLabel = (user: OrgUser | undefined, fallback = 'Unknown user'): string => {
    if (!user) return fallback;
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    return name || user.email || fallback;
};

export const jobSubject = (job: Pick<Job, 'tenant' | 'owner'>): string =>
    job.tenant?.name || job.tenant?.external_id || job.owner?.display_name || job.owner?.email || job.owner?.external_id || '—';

export const personLabel = (person?: JobPerson | null): string =>
    person?.display_name || person?.email || person?.external_id || '—';

// ---------------------------------------------------------------------------
// Catalogs + labels
// ---------------------------------------------------------------------------

// Aimdoc data tools, scoped to the job's customer contact — a job can see that
// person's sessions and journey and their account's facts, never other
// contacts' data.
export const NATIVE_TOOL_CATALOG: { name: string; label: string; description: string }[] = [
    { name: 'get_contact', label: 'Contact details', description: 'Who the job is for' },
    { name: 'list_contact_conversations', label: 'Contact conversations', description: "The customer's conversations" },
    { name: 'get_conversation', label: 'Conversation transcript', description: "A transcript (this contact's only)" },
    { name: 'get_contact_journey', label: 'Contact journey', description: "The customer's activity journey" },
    { name: 'get_account', label: 'Account details', description: "Their account's basic facts" },
    { name: 'get_account_activity', label: 'Account activity', description: 'Account activity trends' },
    { name: 'email_contact', label: 'Email the customer', description: 'Send the customer (and colleagues at their company) an email; replies reach the job' },
];

export const APPROVAL_POLICIES: { value: ApprovalPolicy; label: string; description: string }[] = [
    { value: 'auto', label: 'Auto', description: 'The job may call it without asking.' },
    { value: 'ask_owner', label: 'Ask the customer', description: 'The customer approves each call.' },
    { value: 'ask_vendor', label: 'Ask your team', description: 'Someone on your team approves each call (Needs you).' },
];

export const ACQUISITION_OPTIONS: { value: Acquisition; label: string; description: string }[] = [
    { value: 'vendor_assigned', label: 'Assigned by you', description: 'Only your team starts jobs, from the dashboard or the API.' },
    { value: 'included', label: 'Included', description: 'Every identified customer gets a job automatically.' },
    { value: 'request', label: 'On request', description: 'Customers (or your agent) can ask; you assign.' },
    { value: 'stripe', label: 'Stripe checkout', description: 'Customers subscribe or pay through your connected Stripe account.' },
];

export const SCOPE_OPTIONS: { value: JobScope; label: string; description: string }[] = [
    { value: 'tenant', label: 'Workspace', description: 'One job per customer workspace, with one accountable customer contact.' },
    { value: 'user', label: 'Person', description: 'One job per customer user.' },
];

export const COMPLETION_OPTIONS: { value: Completion; label: string; description: string }[] = [
    { value: 'standing', label: 'Standing', description: 'Never ended by the AI; it idles when there is nothing to do.' },
    { value: 'ai', label: 'Bounded', description: 'The AI ends the job when the declared goal is reached.' },
];

export const BLOCKED_ON_LABELS: Record<BlockedOn, string> = {
    owner_input: 'Waiting on customer',
    steward_guidance: 'Needs your guidance',
    approval: 'Needs approval',
    budget: 'Budget paused',
    owner_missing: 'Needs a customer contact',
};

export const blockedOnLabel = (value?: string | null): string =>
    (value && BLOCKED_ON_LABELS[value as BlockedOn]) || (value ? value.replaceAll('_', ' ') : '');

export const JOB_STATUS_LABELS: Record<JobStatus, string> = { on: 'On', off: 'Off' };

export const ENDED_BY_LABELS: Record<EndedBy, string> = {
    ai: 'the AI (goal reached)',
    owner: 'the customer',
    vendor: 'you',
    subscription: 'the subscription',
};

export const EVENT_KIND_LABELS: Record<JobEventKind, string> = {
    state_changed: 'State updated',
    action: 'Action',
    artifact_published: 'Artifact published',
    asked_owner: 'Asked the customer',
    asked_steward: 'Asked you',
    approval_requested: 'Approval requested',
    approval_decided: 'Approval decided',
    looped_in: 'Looped someone in',
    messaged: 'Job replied',
    blocked: 'Waiting',
    unblocked: 'Resumed',
    steered: 'Steered',
    guided: 'Guidance',
    answered: 'Answered',
    routine_changed: 'Routine changed',
    wake_started: 'Wake started',
    wake_finished: 'Wake finished',
    wake_failed: 'Wake failed',
    wake_scheduled: 'Wake scheduled',
    turned_on: 'Turned on',
    turned_off: 'Turned off',
    owner_changed: 'Customer contact changed',
    memory_changed: 'Memory updated',
    shadow_action: 'Would have done (shadow)',
    shadow_message: 'Would have sent (shadow)',
    mode_changed: 'Mode changed',
    restarted: 'Went live (clean slate)',
    note: 'Note',
};

export const JOB_MODE_OPTIONS: { value: JobMode; label: string; description: string }[] = [
    { value: 'live', label: 'Live', description: 'The job acts: writes happen and the customer hears from it.' },
    {
        value: 'shadow',
        label: 'Shadow',
        description: 'The job rehearses: it works as if live, but every write and customer message is recorded as "would have" for your review instead of happening.',
    },
];

export const eventKindLabel = (kind?: string | null): string =>
    (kind && EVENT_KIND_LABELS[kind as JobEventKind]) || (kind ? kind.replaceAll('_', ' ') : 'Event');

export const WAKE_REASON_LABELS: Record<string, string> = {
    routine: 'Routine',
    event: 'Event',
    self: 'Self-scheduled',
    routine_request: 'Routine request',
    message: 'Message',
    input: 'Answer',
    guidance: 'Guidance',
    approval: 'Approval',
    setup: 'Setup',
    maintenance: 'Upkeep',
    manual: 'Manual',
    reactivated: 'Reactivated',
};

export const wakeReasonLabel = (reason?: string | null): string =>
    (reason && WAKE_REASON_LABELS[reason]) || (reason ? reason.replaceAll('_', ' ') : '—');

export const MESSAGE_AUTHOR_LABELS: Record<string, string> = {
    owner: 'Customer',
    member: 'Workspace member',
    steward: 'You',
    agent: 'Agent',
    job: 'Job',
};

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** Mirrors routine_service.describe_trigger. */
export const describeTrigger = (trigger?: RoutineTrigger | null): string => {
    if (!trigger) return 'no trigger';
    if (trigger.type === 'event') return `on event ${trigger.name}`;
    const suffix = trigger.timezone ? ` ${trigger.timezone}` : '';
    switch (trigger.frequency) {
        case 'interval':
            return `every ${trigger.interval_minutes ?? '?'} min`;
        case 'hourly':
            return `hourly at :${(trigger.at || '00:00').split(':')[1] || '00'}`;
        case 'daily':
            return `daily at ${trigger.at || '—'}${suffix}`;
        case 'weekly': {
            const day = typeof trigger.weekday === 'number' ? WEEKDAYS[trigger.weekday]?.slice(0, 3) : '?';
            return `every ${day} at ${trigger.at || '—'}${suffix}`;
        }
        case 'monthly':
            return `monthly on day ${trigger.day_of_month ?? '?'} at ${trigger.at || '—'}${suffix}`;
        default:
            return JSON.stringify(trigger);
    }
};

/** Render a headline template ("{a} / {b} done") with the job's state. */
export const renderHeadline = (template: string | null | undefined, state: Record<string, any>): string | null => {
    if (!template) return null;
    return template.replace(/\{([a-zA-Z][a-zA-Z0-9_]*)[^}]*\}/g, (_match, name: string) => {
        const value = state?.[name];
        if (value === undefined || value === null) return '—';
        return Array.isArray(value) ? value.join(', ') : String(value);
    });
};

// Mirrors backend slugify_key.
export const slugifyKey = (value: string): string =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 48);

// ---------------------------------------------------------------------------
// Badges + formatting
// ---------------------------------------------------------------------------

const GREEN = 'text-green-700 dark:text-green-400 bg-green-500/10';
const AMBER = 'text-amber-700 dark:text-amber-400 bg-amber-500/10';
const RED = 'text-red-700 dark:text-red-400 bg-red-500/10';
const BLUE = 'text-blue-700 dark:text-blue-400 bg-blue-500/10';
const MUTED = 'text-muted-foreground bg-muted';
const PURPLE = 'text-purple-700 dark:text-purple-400 bg-purple-500/10';

const STATUS_TONES: Record<string, { label: string; tone: string; pulse?: boolean }> = {
    draft: { label: 'Draft', tone: MUTED },
    published: { label: 'Published', tone: GREEN },
    unpublished: { label: 'Unpublished', tone: AMBER },
    active: { label: 'Active', tone: GREEN },
    disabled: { label: 'Disabled', tone: MUTED },
    archived: { label: 'Archived', tone: MUTED },
    // jobs
    on: { label: 'On', tone: GREEN },
    off: { label: 'Off', tone: MUTED },
    blocked: { label: 'Waiting', tone: AMBER },
    working: { label: 'Working', tone: BLUE, pulse: true },
    shadow: { label: 'Shadow', tone: PURPLE },
    // wakes (agent_runs.status)
    succeeded: { label: 'Succeeded', tone: GREEN },
    failed: { label: 'Failed', tone: RED },
    running: { label: 'Running', tone: BLUE, pulse: true },
    pending: { label: 'Queued', tone: BLUE, pulse: true },
    queued: { label: 'Queued', tone: BLUE, pulse: true },
    waiting_input: { label: 'Waiting', tone: AMBER },
    waiting: { label: 'Waiting', tone: AMBER },
    cancelled: { label: 'Cancelled', tone: AMBER },
    skipped: { label: 'Skipped', tone: MUTED },
    paused: { label: 'Paused', tone: AMBER },
    removed: { label: 'Removed', tone: MUTED },
    // approvals / guidance
    approved: { label: 'Approved', tone: GREEN },
    rejected: { label: 'Declined', tone: RED },
    executed: { label: 'Executed', tone: GREEN },
    expired: { label: 'Expired', tone: MUTED },
    open: { label: 'Open', tone: AMBER },
    answered: { label: 'Answered', tone: GREEN },
    defaulted: { label: 'Defaulted', tone: MUTED },
    withdrawn: { label: 'Withdrawn', tone: MUTED },
    // visibility
    vendor: { label: 'Internal', tone: MUTED },
    owner: { label: 'Customer contact only', tone: BLUE },
    tenant: { label: 'Workspace', tone: GREEN },
};

export const ServiceStatusBadge: React.FC<{ status?: string | null; label?: string }> = ({ status, label }) => {
    const config = (status && STATUS_TONES[status]) || {
        label: status || '—',
        tone: MUTED,
    };
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-xl px-2 py-0.5 text-xs font-semibold ${config.tone}`}
        >
            {config.pulse && <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />}
            {label ?? config.label}
        </span>
    );
};

/** On / Off pill plus the on-and-blocked sub-state badge. */
export const JobStatusBadges: React.FC<{ job: Pick<Job, 'status' | 'blocked_on' | 'active_wake_id'> & { mode?: JobMode } }> = ({ job }) => (
    <span className="inline-flex items-center gap-1.5">
        <ServiceStatusBadge status={job.status} />
        {job.mode === 'shadow' ? <ServiceStatusBadge status="shadow" /> : null}
        {job.status === 'on' && job.active_wake_id ? <ServiceStatusBadge status="working" /> : null}
        {job.status === 'on' && job.blocked_on ? (
            <ServiceStatusBadge status="blocked" label={blockedOnLabel(job.blocked_on)} />
        ) : null}
    </span>
);

/** Publication state of a service, derived from its version pointers. */
export const publicationLabel = (service: Pick<Service, 'published_version_number' | 'has_draft'>): string => {
    if (service.published_version_number == null) return 'unpublished';
    return service.has_draft ? 'draft' : 'published';
};

export const describePublication = (service: Pick<Service, 'published_version_number' | 'has_draft'>): string => {
    if (service.published_version_number == null) return 'Not published';
    return service.has_draft
        ? `v${service.published_version_number} · unpublished changes`
        : `v${service.published_version_number}`;
};

/** Credits billed. Sub-credit values show "<1" rather than a misleading "0";
 *  null (not yet metered) renders as a dash. */
export const formatCredits = (credits?: number | null): string => {
    if (credits === null || credits === undefined) return '—';
    if (credits > 0 && credits < 1) return '<1';
    return Math.round(credits).toLocaleString();
};

export const formatDuration = (startedAt?: string | null, completedAt?: string | null): string => {
    if (!startedAt) return '—';
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    const seconds = Math.max(0, Math.round((end - new Date(startedAt).getTime()) / 1000));
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
};

export const formatRelativeTime = (value?: string | null): string => {
    if (!value) return '—';
    const then = new Date(value).getTime();
    if (!Number.isFinite(then)) return '—';
    const deltaSeconds = Math.round((Date.now() - then) / 1000);
    if (deltaSeconds < -60) {
        const ahead = -deltaSeconds;
        if (ahead < 3600) return `in ${Math.floor(ahead / 60)}m`;
        if (ahead < 86400) return `in ${Math.floor(ahead / 3600)}h`;
        return `in ${Math.floor(ahead / 86400)}d`;
    }
    if (deltaSeconds < 60) return 'just now';
    if (deltaSeconds < 3600) return `${Math.floor(deltaSeconds / 60)}m ago`;
    if (deltaSeconds < 86400) return `${Math.floor(deltaSeconds / 3600)}h ago`;
    if (deltaSeconds < 86400 * 30) return `${Math.floor(deltaSeconds / 86400)}d ago`;
    return new Date(value).toLocaleDateString();
};

export const apiErrorMessage = (error: any, fallback: string): string => {
    const detail = error?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail.message === 'string') return detail.message;
    if (typeof error?.message === 'string') return error.message;
    return fallback;
};

// ---------------------------------------------------------------------------
// Shared form pieces
// ---------------------------------------------------------------------------

// Renders a flat input schema (guidance input_schema) as form fields. Values
// state is owned by the caller.
export const SchemaInputFields: React.FC<{
    schema?: ServiceSchema | null;
    values: Record<string, any>;
    onChange: (updater: (prev: Record<string, any>) => Record<string, any>) => void;
}> = ({ schema, values, onChange }) => {
    const properties = schema?.properties || {};
    const required = new Set(schema?.required || []);
    return (
        <>
            {Object.entries(properties).map(([name, spec]) => (
                <div key={name} className="space-y-1">
                    <Label className="text-sm">
                        {name}
                        {required.has(name) && <span className="text-red-500 ml-0.5">*</span>}
                    </Label>
                    {spec.description && <p className="text-xs text-muted-foreground">{spec.description}</p>}
                    {spec.type === 'array' && spec.enum?.length ? (
                        <div className="space-y-1.5 pt-1">
                            {spec.enum.map((option) => {
                                const key = String(option);
                                const selected = Array.isArray(values[name]) ? (values[name] as string[]) : [];
                                const checked = selected.includes(key);
                                return (
                                    <label key={key} className="flex items-start gap-2 text-sm">
                                        <Checkbox
                                            checked={checked}
                                            onCheckedChange={(next: boolean | 'indeterminate') =>
                                                onChange((prev) => {
                                                    const current = Array.isArray(prev[name]) ? (prev[name] as string[]) : [];
                                                    return {
                                                        ...prev,
                                                        [name]: next === true
                                                            ? [...current.filter((v) => v !== key), key]
                                                            : current.filter((v) => v !== key),
                                                    };
                                                })
                                            }
                                        />
                                        <span>{key}</span>
                                    </label>
                                );
                            })}
                        </div>
                    ) : spec.type === 'array' ? (
                        <Input
                            placeholder="Comma-separated values"
                            value={Array.isArray(values[name]) ? (values[name] as string[]).join(', ') : values[name] ?? ''}
                            onChange={(event) => {
                                const raw = event.target.value;
                                onChange((prev) => ({
                                    ...prev,
                                    [name]: raw.split(',').map((v) => v.trim()).filter(Boolean),
                                }));
                            }}
                        />
                    ) : spec.type === 'boolean' ? (
                        <div className="pt-1">
                            <Switch
                                checked={Boolean(values[name] ?? spec.default)}
                                onCheckedChange={(checked) => onChange((prev) => ({ ...prev, [name]: checked }))}
                            />
                        </div>
                    ) : spec.enum?.length ? (
                        <Select
                            value={values[name] != null ? String(values[name]) : ''}
                            onValueChange={(value) => onChange((prev) => ({ ...prev, [name]: value }))}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder={spec.default != null ? `Default: ${spec.default}` : 'Select…'} />
                            </SelectTrigger>
                            <SelectContent>
                                {spec.enum.map((option) => (
                                    <SelectItem key={String(option)} value={String(option)}>
                                        {String(option)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            type={spec.type === 'string' ? 'text' : 'number'}
                            placeholder={spec.default != null ? `Default: ${spec.default}` : undefined}
                            value={values[name] ?? ''}
                            onChange={(event) => {
                                const raw = event.target.value;
                                onChange((prev) => ({
                                    ...prev,
                                    [name]:
                                        spec.type === 'string'
                                            ? raw
                                            : raw === ''
                                                ? ''
                                                : spec.type === 'integer'
                                                    ? parseInt(raw, 10)
                                                    : Number(raw),
                                }));
                            }}
                        />
                    )}
                </div>
            ))}
        </>
    );
};

export const cleanInputValues = (values: Record<string, any>): Record<string, any> => {
    const cleaned: Record<string, any> = {};
    Object.entries(values).forEach(([key, value]) => {
        if (value !== '' && value != null) cleaned[key] = value;
    });
    return cleaned;
};
